/**
 * Loyalty earning rules -- phase 6b, Task 5.
 *
 * GET/PUT /admin/discounts/loyalty/config: the single `loyalty_config` row.
 * The R500 threshold is DATA here, never a constant (spec §0) -- the previous
 * threshold was wrong for four years because it lived in a Voucherify rule
 * nobody could read, so the rule is stated on the screen under the field.
 *
 * Money crosses the boundary as a 2dp string: the input holds what the admin
 * typed and `toMoneyString` normalises (or refuses) it on Save. The integer
 * fields are parsed as whole numbers; ranges are the API's (1..100, 1..1000,
 * 1..120) and a 422 lands on the field its path names.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { toMoneyString } from '../money';
import type { LoyaltyConfig, LoyaltyConfigBody } from '../types';

export const THRESHOLD_NOTE = 'Points are earned on orders whose subtotal (excluding delivery, fees and tips) is at least this amount.';
export const POINTS_PER_REWARD_NOTE =
  "Lowering this makes existing balances eligible; rewards are issued on the customer's next earn or adjustment, not immediately.";

type Draft = { earnThresholdAmount: string; earnPointsPerOrder: string; pointsPerReward: string; pointsExpiryMonths: string };
type IntField = Exclude<keyof Draft, 'earnThresholdAmount'>;

const INT_FIELDS: Array<{ key: IntField; label: string; min: number; max: number; note?: string }> = [
  { key: 'earnPointsPerOrder', label: 'Points per qualifying order', min: 1, max: 100 },
  { key: 'pointsPerReward', label: 'Points per reward', min: 1, max: 1000, note: POINTS_PER_REWARD_NOTE },
  { key: 'pointsExpiryMonths', label: 'Points expiry (months)', min: 1, max: 120 },
];

/** Draft -> API body, or the per-field reasons it cannot be sent. */
export function toConfigBody(draft: Draft): { body?: LoyaltyConfigBody; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  let earnThresholdAmount: string | null = null;
  try {
    earnThresholdAmount = toMoneyString(draft.earnThresholdAmount);
    if (earnThresholdAmount === null) errors.earnThresholdAmount = 'Required';
  } catch (e) {
    errors.earnThresholdAmount = (e as Error).message;
  }
  const ints = {} as Record<IntField, number>;
  INT_FIELDS.forEach(({ key, min, max }) => {
    const s = draft[key].trim();
    const n = /^\d+$/.test(s) ? Number(s) : NaN;
    if (!Number.isInteger(n)) errors[key] = 'Enter a whole number';
    else if (n < min || n > max) errors[key] = `Must be between ${min} and ${max}`;
    else ints[key] = n;
  });
  if (Object.keys(errors).length) return { errors };
  return { body: { earnThresholdAmount: earnThresholdAmount as string, ...ints }, errors };
}

const toDraft = (c: LoyaltyConfig): Draft => ({
  earnThresholdAmount: c.earnThresholdAmount,
  earnPointsPerOrder: String(c.earnPointsPerOrder),
  pointsPerReward: String(c.pointsPerReward),
  pointsExpiryMonths: String(c.pointsExpiryMonths),
});

const ConfigFields: React.FC<{ config: LoyaltyConfig; readOnly: boolean }> = ({ config, readOnly }) => {
  const notify = useNotify();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => toDraft(config));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: discountsApi.putLoyaltyConfig,
    onSuccess: () => {
      setFieldErrors({});
      setFormError(null);
      notify('Loyalty configuration saved.', { type: 'success' });
      qc.invalidateQueries({ queryKey: qk.loyaltyConfig });
    },
    onError: (err) => {
      const failure = toApiFailure(err);
      if (failure.status === 422) {
        setFieldErrors(failure.fieldErrors);
        setFormError(Object.keys(failure.fieldErrors).length ? null : failure.message);
      } else {
        notify(failure.message, { type: 'error' });
      }
    },
  });

  const set = (key: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setDraft((d) => ({ ...d, [key]: value }));
    setFieldErrors((f) => (f[key] ? { ...f, [key]: '' } : f));
  };

  const onSave = () => {
    const { body, errors } = toConfigBody(draft);
    setFieldErrors(errors);
    setFormError(null);
    if (body) save.mutate(body);
  };

  return (
    <Stack spacing={2} component="form" onSubmit={(e: React.FormEvent) => e.preventDefault()} data-testid="loyalty-config-form">
      {formError && <Alert severity="error">{formError}</Alert>}
      <TextField
        id="loyalty-earn-threshold"
        label="Earn threshold (R)"
        value={draft.earnThresholdAmount}
        onChange={set('earnThresholdAmount')}
        error={Boolean(fieldErrors.earnThresholdAmount)}
        helperText={fieldErrors.earnThresholdAmount || THRESHOLD_NOTE}
        slotProps={{ input: { readOnly }, htmlInput: { inputMode: 'decimal' } }}
        fullWidth
      />
      {INT_FIELDS.map(({ key, label, min, max, note }) => (
        <TextField
          key={key}
          id={`loyalty-${key}`}
          label={label}
          type="number"
          value={draft[key]}
          onChange={set(key)}
          error={Boolean(fieldErrors[key])}
          helperText={fieldErrors[key] || note || `Between ${min} and ${max}.`}
          slotProps={{ input: { readOnly }, htmlInput: { min, max, step: 1 } }}
          fullWidth
        />
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          Last updated {new Date(config.updatedAt).toLocaleString()}
          {config.updatedBy ? ` by ${config.updatedBy}` : ''}
        </Typography>
        {!readOnly && (
          <Button variant="contained" onClick={onSave} disabled={save.isPending}>
            Save earning rules
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export const ConfigForm: React.FC<{ readOnly: boolean }> = ({ readOnly }) => {
  const { data, isLoading, error } = useQuery({ queryKey: qk.loyaltyConfig, queryFn: discountsApi.loyaltyConfig });

  if (error) {
    return <Alert severity="error">Could not load loyalty configuration: {toApiFailure(error).message}</Alert>;
  }
  if (isLoading || !data) {
    return <Skeleton variant="rounded" height={260} data-testid="loyalty-config-loading" />;
  }
  // Keyed on updatedAt so a fresh row after a save (or a refetch) replaces the
  // draft, while a refetch of the same row leaves in-progress edits alone.
  return <ConfigFields key={data.updatedAt} config={data} readOnly={readOnly} />;
};

export default ConfigForm;
